import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  IconAlertTriangle,
  IconCheck,
  IconClock,
  IconLeaf,
  IconPhoto,
  IconRefresh,
  IconScanEye,
  IconX,
} from "@tabler/icons-react";

import { api, ApiError } from "../lib/api";
import { BunchVisionWorkspace } from "../components/BunchVisionWorkspace";
import {
  classifyBunch,
  OLC_UI_OPTIONS,
  MESOCARP_UI_OPTIONS,
  SOCKET_BUCKETS,
  socketBucketFor,
  type Olc,
  type Mesocarp,
  type RipenessClass,
} from "../lib/estate_ripeness_classifier";

const API_BASE = import.meta.env.VITE_API_URL ?? "";

/**
 * Smart Grading — office / manager view of estate-side AI grading activity.
 *
 * The Mandor mobile app (/mandor) is where field capture happens. This
 * desktop page is what the Estate Manager or Assistant Manager watches to:
 *   - See today's TPH bunch audits with RGCQ signals
 *   - Verify "ready for harvest" call rate (avg sockets ≥ 5)
 *   - Watch NFC delivery cards written and their scan status
 *   - Write cards from the office when a truck is loaded but Mandor's
 *     phone is offline
 */

interface BunchAudit {
  id: string;
  tphId: string;
  mandorWorkerId: string | null;
  gangId: string | null;
  mode: string;
  auditDate: string;
  createdAt: string;
  totalBunches: number;
  ripeCount: number;
  underripeCount: number;
  unripeCount: number;
  overripeCount: number;
  emptyCount: number;
  rottenCount: number;
  socketCount: number;
  exocarpColour: string | null;
  mesocarpColour: string | null;
  readyForHarvest: string | null;
  defectFlags: Record<string, boolean> | null;
  ophCode: string | null;
  rgcqRawSignals: {
    model?: string;
    confidence?: number;
    latency_ms?: number;
    raw_prediction?: Record<string, unknown>;
    per_bunch?: Array<{
      bunch_index: number;
      detached_sockets: number;
      detached_pct?: number;
      olc: string | null;
      mesocarp: string | null;
      defect_flags?: Record<string, boolean>;
      classified_as?: string;
      classifier_reason?: string;
    }>;
    quality_state_counts?: Partial<Record<QualityKey, number>>;
  } | null;
  notes: string | null;
}

interface NfcCard {
  id: string;
  cardCode: string;
  status: string;
  totalBunches: number;
  totalReadyForHarvest: number;
  totalPreRejected: number;
  writtenAt: string;
  scannedAt: string | null;
  estateId: string;
  divisionId: string;
  primaryBlockId: string | null;
  gradingSummary: {
    grade_counts?: Record<string, number>;
    defect_flag_totals?: Record<string, number>;
    avg_socket_count?: number;
  } | null;
}

interface Tph {
  id: string;
  code: string;
  blockId: string;
}

interface Block {
  id: string;
  code: string;
  divisionId: string;
}

export function SmartGrading() {
  const qc = useQueryClient();
  const [selectedAudits, setSelectedAudits] = useState<string[]>([]);
  const [writeCardMsg, setWriteCardMsg] = useState<string | null>(null);
  const [filterMode, setFilterMode] = useState<"ALL" | "AI" | "MANUAL">("ALL");
  const [filterReady, setFilterReady] = useState<
    "ALL" | "YES" | "NO" | "MIXED"
  >("ALL");
  const [filterDate, setFilterDate] = useState<
    "TODAY" | "YESTERDAY" | "LAST7" | "ALL"
  >("TODAY");
  const [filterTph, setFilterTph] = useState<string>("ALL");
  const [filterMandor, setFilterMandor] = useState<string>("ALL");
  const [filterTime, setFilterTime] = useState<
    "ALL" | "MORNING" | "AFTERNOON" | "EVENING"
  >("ALL");
  const [filterHarvester, setFilterHarvester] = useState<string>("ALL");
  const [scanResultAudit, setScanResultAudit] = useState<BunchAudit | null>(
    null,
  );
  // Per-card row action state · which card's action panel is open, and
  // in what mode (view / edit / reject).
  const [cardAction, setCardAction] = useState<{
    card: NfcCard;
    mode: "view" | "edit" | "reject";
  } | null>(null);
  const [cardMsg, setCardMsg] = useState<string | null>(null);

  const auditsQ = useQuery({
    queryKey: ["bunch-audits-grading"],
    staleTime: 0,
    refetchOnMount: true,
    queryFn: () => api.list<BunchAudit>("/v1/bunch-audits"),
  });
  const cardsQ = useQuery({
    queryKey: ["nfc-delivery-cards"],
    staleTime: 0,
    refetchOnMount: true,
    queryFn: () => api.list<NfcCard>("/v1/nfc-delivery-cards"),
  });
  const tphsQ = useQuery({
    queryKey: ["tphs"],
    queryFn: () => api.list<Tph>("/v1/tphs"),
  });
  const blocksQ = useQuery({
    queryKey: ["blocks"],
    queryFn: () =>
      api.list<Block>("/v1/blocks?limit=500"),
  });
  const workersQ = useQuery({
    queryKey: ["workers"],
    queryFn: () =>
      api.list<{ id: string; name: string; workerType: string }>(
        "/v1/workers?limit=500",
      ),
  });

  const tphById = useMemo(() => {
    const m = new Map<string, Tph>();
    for (const t of tphsQ.data?.data ?? []) m.set(t.id, t);
    return m;
  }, [tphsQ.data]);
  const blockById = useMemo(() => {
    const m = new Map<string, Block>();
    for (const b of blocksQ.data?.data ?? []) m.set(b.id, b);
    return m;
  }, [blocksQ.data]);
  const workerById = useMemo(() => {
    const m = new Map<
      string,
      { id: string; name: string; workerType: string }
    >();
    for (const w of workersQ.data?.data ?? []) m.set(w.id, w);
    return m;
  }, [workersQ.data]);

  const audits = auditsQ.data?.data ?? [];
  const cards = cardsQ.data?.data ?? [];

  // Distinct dropdown options built from what actually exists in the data.
  const uniqueMandors = useMemo(
    () =>
      Array.from(
        new Set(audits.map((a) => a.mandorWorkerId).filter(Boolean) as string[]),
      ),
    [audits],
  );
  const uniqueTphs = useMemo(
    () => Array.from(new Set(audits.map((a) => a.tphId))),
    [audits],
  );
  // Harvester list = every worker of type HARVESTER (workers query gives us
  // all; we let the office user pick anyone, unlike mobile which scopes to
  // just today's gang roster).
  const harvesterOptions = useMemo(
    () =>
      (workersQ.data?.data ?? []).filter((w) => w.workerType === "HARVESTER"),
    [workersQ.data],
  );

  const dayStart = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const dayEnd = new Date(dayStart.getTime() + 86_400_000);
  const yStart = new Date(dayStart.getTime() - 86_400_000);
  const last7Start = new Date(dayStart.getTime() - 6 * 86_400_000);

  const filteredAudits = audits.filter((a) => {
    if (filterMode !== "ALL" && a.mode !== filterMode) return false;
    if (filterReady !== "ALL" && (a.readyForHarvest ?? "NO") !== filterReady)
      return false;
    const d = new Date(a.auditDate);
    if (filterDate === "TODAY" && (d < dayStart || d >= dayEnd)) return false;
    if (filterDate === "YESTERDAY" && (d < yStart || d >= dayStart))
      return false;
    if (filterDate === "LAST7" && d < last7Start) return false;
    if (filterMandor !== "ALL" && a.mandorWorkerId !== filterMandor)
      return false;
    if (filterTph !== "ALL" && a.tphId !== filterTph) return false;
    if (filterTime !== "ALL") {
      const h = d.getHours();
      if (filterTime === "MORNING" && !(h >= 6 && h < 12)) return false;
      if (filterTime === "AFTERNOON" && !(h >= 12 && h < 18)) return false;
      if (filterTime === "EVENING" && !(h >= 18 || h < 6)) return false;
    }
    // Harvester filter: audits are gang-scoped, so match only when the
    // audit's gangId is set AND the selected harvester is a harvester in
    // the system (we don't have per-audit gang→harvester joins client-
    // side; office user filters by "audits from a gang" here).
    if (filterHarvester !== "ALL" && !a.gangId) return false;
    return true;
  });

  const todayCount = audits.filter((a) => {
    const d = new Date(a.auditDate);
    const now = new Date();
    return (
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate()
    );
  }).length;

  const readyRate = audits.length
    ? Math.round(
        (audits.filter((a) => a.readyForHarvest === "YES").length /
          audits.length) *
          100,
      )
    : 0;

  const cardsWritten = cards.length;
  const cardsScanned = cards.filter(
    (c) => c.status === "SCANNED" || c.status === "RECONCILED",
  ).length;

  const toggleAudit = (id: string) =>
    setSelectedAudits((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const writeCard = async () => {
    setWriteCardMsg(null);
    if (!selectedAudits.length) return;
    // Resolve division from the first audit's TPH → block → divisionId.
    const firstAudit = audits.find((a) => a.id === selectedAudits[0]);
    if (!firstAudit) return;
    const tph = tphById.get(firstAudit.tphId);
    const block = tph ? blockById.get(tph.blockId) : null;
    if (!block) {
      setWriteCardMsg("Cannot resolve division — TPH or block metadata missing.");
      return;
    }
    try {
      const res = await api.post<{ cardCode: string; totalBunches: number }>(
        "/v1/nfc-delivery-cards",
        {
          divisionId: block.divisionId,
          primaryBlockId: block.id,
          bunchAuditBatchIds: selectedAudits,
        },
      );
      setWriteCardMsg(
        `NFC card written ✓ · ${res.data.cardCode} · ${res.data.totalBunches} bunches`,
      );
      setSelectedAudits([]);
      qc.invalidateQueries({ queryKey: ["nfc-delivery-cards"] });
    } catch (err) {
      setWriteCardMsg(err instanceof ApiError ? err.message : String(err));
    }
  };

  return (
    <div className="space-y-5 text-sm">
      {/* ─── Header ─────────────────────────────────────────────── */}
      <div>
        <h1 className="flex items-center gap-2 text-lg font-medium">
          <IconScanEye size={20} className="text-emerald-700" />
          Smart Grading · Estate
        </h1>
        <p className="text-xs text-text-secondary">
          RGCQ-based bunch grading at the TPH platform. Field capture happens on
          the{" "}
          <a href="/mandor" className="underline">
            Mandor mobile
          </a>
          ; this page is the office view of what's being graded and which cards
          are ready for the mill weighbridge.
        </p>
      </div>

      <BunchVisionWorkspace />

      {/* ─── KPI tiles ──────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Kpi
          icon={<IconLeaf size={16} className="text-emerald-700" />}
          label="Audits today"
          value={String(todayCount)}
          hint={`${audits.length} total loaded`}
        />
        <Kpi
          icon={<IconCheck size={16} className="text-emerald-700" />}
          label="Ready-for-harvest rate"
          value={`${readyRate}%`}
          hint={`≥5 sockets/bunch (${audits.filter((a) => a.readyForHarvest === "YES").length}/${audits.length})`}
        />
        <Kpi
          icon={<IconRefresh size={16} className="text-blue-700" />}
          label="NFC cards written"
          value={String(cardsWritten)}
          hint={`${cardsScanned} scanned at mill`}
        />
        <Kpi
          icon={<IconAlertTriangle size={16} className="text-amber-700" />}
          label="Pre-rejected bunches"
          value={String(
            cards.reduce((n, c) => n + (c.totalPreRejected ?? 0), 0),
          )}
          hint="Unripe/Empty/Overripe/Rotten flagged at estate"
        />
      </div>

      {/* ─── Filters ────────────────────────────────────────────── */}
      <div className="rounded border border-border bg-surface-2 p-2 text-xs space-y-2">
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-1.5">
            <span className="text-text-secondary">Mode:</span>
            <select
              value={filterMode}
              onChange={(e) =>
                setFilterMode(e.target.value as typeof filterMode)
              }
              className="rounded border border-border bg-surface-0 px-2 py-1"
            >
              <option value="ALL">All</option>
              <option value="AI">AI</option>
              <option value="MANUAL">Manual</option>
            </select>
          </label>
          <label className="flex items-center gap-1.5">
            <span className="text-text-secondary">Ready for harvest:</span>
            <select
              value={filterReady}
              onChange={(e) =>
                setFilterReady(e.target.value as typeof filterReady)
              }
              className="rounded border border-border bg-surface-0 px-2 py-1"
            >
              <option value="ALL">All</option>
              <option value="YES">Yes (≥5 sockets)</option>
              <option value="NO">No (0 sockets)</option>
              <option value="MIXED">Mixed (&lt;5 avg)</option>
            </select>
          </label>
          <span className="text-text-secondary">
            {filteredAudits.length}/{audits.length}
          </span>
          <button
            onClick={() => {
              qc.invalidateQueries({ queryKey: ["bunch-audits-grading"] });
              qc.invalidateQueries({ queryKey: ["nfc-delivery-cards"] });
            }}
            className="ml-auto flex items-center gap-1 rounded border border-border px-2 py-1 hover:bg-surface-1"
          >
            <IconRefresh size={13} /> Refresh
          </button>
        </div>
        {/* Row 2 — the 5 audit-scope filters */}
        <div className="flex flex-wrap items-center gap-3 border-t border-border pt-2">
          <label className="flex items-center gap-1.5">
            <span className="text-text-secondary">Date:</span>
            <select
              value={filterDate}
              onChange={(e) =>
                setFilterDate(e.target.value as typeof filterDate)
              }
              className="rounded border border-border bg-surface-0 px-2 py-1"
            >
              <option value="TODAY">Today</option>
              <option value="YESTERDAY">Yesterday</option>
              <option value="LAST7">Last 7 days</option>
              <option value="ALL">All time</option>
            </select>
          </label>
          <label className="flex items-center gap-1.5">
            <span className="text-text-secondary">Time:</span>
            <select
              value={filterTime}
              onChange={(e) =>
                setFilterTime(e.target.value as typeof filterTime)
              }
              className="rounded border border-border bg-surface-0 px-2 py-1"
            >
              <option value="ALL">All hours</option>
              <option value="MORNING">Morning · 6-12</option>
              <option value="AFTERNOON">Afternoon · 12-18</option>
              <option value="EVENING">Evening · 18-6</option>
            </select>
          </label>
          <label className="flex items-center gap-1.5">
            <span className="text-text-secondary">TPH:</span>
            <select
              value={filterTph}
              onChange={(e) => setFilterTph(e.target.value)}
              className="rounded border border-border bg-surface-0 px-2 py-1 font-mono"
            >
              <option value="ALL">All TPH</option>
              {uniqueTphs.map((id) => (
                <option key={id} value={id}>
                  {tphById.get(id)?.code ?? id.slice(0, 8)}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-1.5">
            <span className="text-text-secondary">Mandor:</span>
            <select
              value={filterMandor}
              onChange={(e) => setFilterMandor(e.target.value)}
              className="rounded border border-border bg-surface-0 px-2 py-1"
            >
              <option value="ALL">All Mandors</option>
              {uniqueMandors.map((id) => (
                <option key={id} value={id}>
                  {workerById.get(id)?.name ?? id.slice(0, 8)}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-1.5">
            <span className="text-text-secondary">Harvester:</span>
            <select
              value={filterHarvester}
              onChange={(e) => setFilterHarvester(e.target.value)}
              className="rounded border border-border bg-surface-0 px-2 py-1"
            >
              <option value="ALL">All harvesters</option>
              {harvesterOptions.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </select>
          </label>
          {(filterDate !== "TODAY" ||
            filterMandor !== "ALL" ||
            filterTph !== "ALL" ||
            filterTime !== "ALL" ||
            filterHarvester !== "ALL") && (
            <button
              onClick={() => {
                setFilterDate("TODAY");
                setFilterMandor("ALL");
                setFilterTph("ALL");
                setFilterTime("ALL");
                setFilterHarvester("ALL");
              }}
              className="ml-auto rounded border border-border px-2 py-1 text-text-secondary hover:bg-surface-1"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* ─── Bunch audits table ─────────────────────────────────── */}
      <section className="rounded border border-border">
        <div className="flex items-center justify-between border-b border-border bg-surface-2 px-3 py-2">
          <div className="font-medium">Bunch audits</div>
          <div className="text-xs text-text-secondary">
            RGCQ signals per batch · tick to include in a new NFC card
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="border-b border-border bg-surface-1 text-text-secondary">
              <tr>
                <th className="p-2 text-left w-8"></th>
                <th className="p-2 text-left">Time</th>
                <th className="p-2 text-left">TPH</th>
                <th className="p-2 text-left">Mode</th>
                <th className="p-2 text-right">Bunches</th>
                <th className="p-2 text-right">Ripe</th>
                <th className="p-2 text-right">Unripe</th>
                <th className="p-2 text-right">Overripe</th>
                <th className="p-2 text-right">Empty</th>
                <th className="p-2 text-right">Rotten</th>
                <th className="p-2 text-left">Ready?</th>
                <th className="p-2 text-left" style={{ minWidth: 220, width: 260 }}>
                  Quality state
                </th>
                <th className="p-2 text-left">OPH</th>
                <th className="p-2 text-left">Scan</th>
              </tr>
            </thead>
            <tbody>
              {filteredAudits.slice(0, 100).map((a) => {
                const tph = tphById.get(a.tphId);
                return (
                  <tr key={a.id} className="border-b border-border hover:bg-surface-1">
                    <td className="p-2">
                      <input
                        type="checkbox"
                        checked={selectedAudits.includes(a.id)}
                        onChange={() => toggleAudit(a.id)}
                      />
                    </td>
                    <td className="p-2 whitespace-nowrap font-mono text-[11px]">
                      {new Date(a.auditDate).toLocaleString("en-MY", {
                        month: "short",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </td>
                    <td className="p-2 font-mono">{tph?.code ?? a.tphId.slice(0, 6)}</td>
                    <td className="p-2">
                      <span
                        className={`rounded px-1.5 py-0.5 text-[9px] font-semibold ${
                          a.mode === "AI"
                            ? "bg-emerald-100 text-emerald-800"
                            : "bg-slate-100 text-slate-700"
                        }`}
                      >
                        {a.mode}
                      </span>
                    </td>
                    <td className="p-2 text-right font-mono tabular-nums">
                      {a.totalBunches}
                    </td>
                    <td className="p-2 text-right font-mono tabular-nums text-emerald-700 font-semibold">
                      {a.ripeCount || "—"}
                    </td>
                    <td className="p-2 text-right font-mono tabular-nums text-blue-600">
                      {a.unripeCount || "—"}
                    </td>
                    <td className="p-2 text-right font-mono tabular-nums text-orange-600">
                      {a.overripeCount || "—"}
                    </td>
                    <td className="p-2 text-right font-mono tabular-nums text-slate-600">
                      {a.emptyCount || "—"}
                    </td>
                    <td className="p-2 text-right font-mono tabular-nums text-red-600">
                      {a.rottenCount || "—"}
                    </td>
                    <td className="p-2">
                      <span
                        className={`rounded px-1.5 py-0.5 text-[9px] font-semibold ${
                          a.readyForHarvest === "YES"
                            ? "bg-emerald-100 text-emerald-800"
                            : a.readyForHarvest === "MIXED"
                              ? "bg-amber-100 text-amber-800"
                              : "bg-slate-100 text-slate-700"
                        }`}
                      >
                        {a.readyForHarvest ?? "—"}
                      </span>
                    </td>
                    <td className="p-2 text-[11px] text-text-secondary align-top" style={{ minWidth: 220 }}>
                      {(() => {
                        const qs = a.rgcqRawSignals?.quality_state_counts ?? {};
                        const parts = QUALITY_KEYS.filter(
                          (k) => (qs[k] ?? 0) > 0,
                        ).map((k) => ({ label: QUALITY_LABELS[k], n: qs[k]! }));
                        if (!parts.length) return "—";
                        return (
                          <div className="flex flex-wrap gap-x-2 gap-y-0.5">
                            {parts.map(({ label, n }) => (
                              <span
                                key={label}
                                className="whitespace-nowrap"
                              >
                                <b className="text-text-primary tabular-nums">
                                  {n}
                                </b>{" "}
                                {label}
                              </span>
                            ))}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="p-2 font-mono text-[11px]">
                      {a.ophCode ?? "—"}
                    </td>
                    <td className="p-2">
                      <button
                        onClick={() => setScanResultAudit(a)}
                        className="flex items-center gap-1 rounded border border-border bg-surface-1 px-2 py-0.5 text-[10px] font-medium text-text-primary hover:bg-brand-green hover:text-white hover:border-brand-green transition-colors"
                        title="Open scan result"
                      >
                        <IconScanEye size={11} />
                        View
                      </button>
                    </td>
                  </tr>
                );
              })}
              {!filteredAudits.length && (
                <tr>
                  <td
                    colSpan={14}
                    className="p-6 text-center text-text-secondary"
                  >
                    No audits match the current filters. Grade a batch on the{" "}
                    <a href="/mandor" className="underline">
                      Mandor mobile
                    </a>{" "}
                    to see it here.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* ─── Write NFC card from office ─────────────────────────── */}
      {selectedAudits.length > 0 && (
        <div className="rounded border border-emerald-300 bg-emerald-50 p-3">
          <div className="flex items-center justify-between">
            <div className="text-xs text-emerald-900">
              {selectedAudits.length} audit(s) selected · ready to write NFC
              delivery card
            </div>
            <button
              onClick={writeCard}
              className="rounded bg-emerald-700 px-3 py-1.5 text-xs font-bold text-white hover:bg-emerald-800"
            >
              Write NFC card
            </button>
          </div>
          {writeCardMsg && (
            <div className="mt-2 text-xs text-emerald-800">{writeCardMsg}</div>
          )}
        </div>
      )}

      {/* ─── Recent NFC cards ───────────────────────────────────── */}
      <section className="rounded border border-border">
        <div className="flex items-center justify-between border-b border-border bg-surface-2 px-3 py-2">
          <div className="font-medium">NFC Delivery Cards · written today</div>
          <div className="text-xs text-text-secondary">
            Scanned status flips when the mill weighbridge reads the card
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="border-b border-border bg-surface-1 text-text-secondary">
              <tr>
                <th className="p-2 text-left">Card code</th>
                <th className="p-2 text-left">Status</th>
                <th className="p-2 text-right">Bunches</th>
                <th className="p-2 text-right">Ready-for-harvest</th>
                <th className="p-2 text-right">Pre-rejected</th>
                <th className="p-2 text-right">Avg sockets</th>
                <th className="p-2 text-left">Written</th>
                <th className="p-2 text-left">Scanned</th>
                <th className="p-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {cards.slice(0, 50).map((c) => (
                <tr key={c.id} className="border-b border-border">
                  <td className="p-2 font-mono">{c.cardCode}</td>
                  <td className="p-2">
                    <span
                      className={`rounded px-1.5 py-0.5 text-[9px] font-semibold ${
                        c.status === "SCANNED"
                          ? "bg-emerald-100 text-emerald-800"
                          : c.status === "WRITTEN"
                            ? "bg-blue-100 text-blue-800"
                            : c.status === "RECONCILED"
                              ? "bg-slate-800 text-white"
                              : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {c.status}
                    </span>
                  </td>
                  <td className="p-2 text-right font-mono tabular-nums">
                    {c.totalBunches}
                  </td>
                  <td className="p-2 text-right font-mono tabular-nums">
                    {c.totalReadyForHarvest}
                  </td>
                  <td className="p-2 text-right font-mono tabular-nums">
                    {c.totalPreRejected}
                  </td>
                  <td className="p-2 text-right font-mono tabular-nums">
                    {c.gradingSummary?.avg_socket_count?.toFixed(1) ?? "—"}
                  </td>
                  <td className="p-2 text-[11px]">
                    {new Date(c.writtenAt).toLocaleString("en-MY", {
                      month: "short",
                      day: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </td>
                  <td className="p-2 text-[11px] text-text-secondary">
                    {c.scannedAt
                      ? new Date(c.scannedAt).toLocaleString("en-MY", {
                          month: "short",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "—"}
                  </td>
                  <td className="p-2">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setCardAction({ card: c, mode: "view" })}
                        className="rounded border border-border bg-surface-1 px-1.5 py-0.5 text-[10px] font-medium hover:bg-brand-green hover:text-white hover:border-brand-green transition-colors"
                        title="View card details"
                      >
                        View
                      </button>
                      <button
                        onClick={() => setCardAction({ card: c, mode: "edit" })}
                        disabled={c.status === "CANCELLED" || c.status === "RECONCILED"}
                        className="rounded border border-border bg-surface-1 px-1.5 py-0.5 text-[10px] font-medium hover:bg-brand-green hover:text-white hover:border-brand-green transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        title={c.status === "CANCELLED" ? "Rejected · read-only" : c.status === "RECONCILED" ? "Reconciled · read-only" : "Edit card notes"}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => setCardAction({ card: c, mode: "reject" })}
                        disabled={c.status === "CANCELLED" || c.status === "RECONCILED"}
                        className="rounded border border-red-300 bg-white px-1.5 py-0.5 text-[10px] font-medium text-red-700 hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        title="Reject / cancel this card"
                      >
                        Reject
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!cards.length && (
                <tr>
                  <td
                    colSpan={9}
                    className="p-6 text-center text-text-secondary"
                  >
                    No delivery cards written yet. Select audits above and click
                    <b> Write NFC card</b>.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {scanResultAudit && (
        <ScanResultModal
          audit={scanResultAudit}
          tphCode={tphById.get(scanResultAudit.tphId)?.code ?? null}
          onClose={() => setScanResultAudit(null)}
          onSaved={(updated) => {
            setScanResultAudit(updated);
            qc.invalidateQueries({ queryKey: ["bunch-audits-grading"] });
            qc.invalidateQueries({ queryKey: ["bunch-audits"] });
          }}
        />
      )}

      {cardAction && (
        <NfcCardActionPanel
          card={cardAction.card}
          mode={cardAction.mode}
          onClose={() => {
            setCardAction(null);
            setCardMsg(null);
          }}
          onSaved={(msg) => {
            setCardMsg(msg);
            setCardAction(null);
            qc.invalidateQueries({ queryKey: ["nfc-delivery-cards"] });
          }}
        />
      )}
      {cardMsg && (
        <div className="fixed bottom-4 right-4 z-40 rounded border border-brand-green bg-white px-3 py-2 text-xs text-brand-green-dark shadow-lg">
          {cardMsg}
        </div>
      )}
    </div>
  );
}

/**
 * Scan result modal · timestamped detail view for a single bunch audit.
 *
 * Renders like a "detection card" from a CV pipeline:
 *   · timestamp header (audit date + created at)
 *   · original scan photo on the left (from GET /:id/photo, if stored)
 *   · statistics + confidence bars on the right (RGCQ signals)
 *   · model + latency footer
 */
const DEFECT_KEYS = [
  "rotten",
  "buah_peram",
  "old",
  "wet",
  "dirty",
  "long_stalk",
  "reject",
] as const;

/**
 * Defect-count categories · counts of how many bunches in the batch
 * have each defect. Independent of ripeness class (a RIPE bunch can
 * also be `wet + dirty`). Persisted under
 * rgcqRawSignals.quality_state_counts for backwards-compat with earlier
 * data; the vocabulary here is now defect-focused as per the estate
 * spec (empty, buah peram, unripe, dirt/contamination, rotten,
 * parthenocarpy, old, dura, wet).
 */
const QUALITY_KEYS = [
  "ripe",
  "underripe",
  "unripe",
  "empty",
  "buah_peram",
  "dirty",
  "rotten",
  "parthenocarpy",
  "old",
  "dura",
  "wet",
] as const;
type QualityKey = (typeof QUALITY_KEYS)[number];
const QUALITY_LABELS: Record<QualityKey, string> = {
  ripe: "Ripe",
  underripe: "Underripe",
  unripe: "Unripe",
  empty: "Empty",
  buah_peram: "Buah peram",
  dirty: "Dirt / contamination",
  rotten: "Rotten",
  parthenocarpy: "Parthenocarpy",
  old: "Old",
  dura: "Dura",
  wet: "Wet",
};

interface EditState {
  ripeCount: number;
  underripeCount: number;
  unripeCount: number;
  overripeCount: number;
  emptyCount: number;
  rottenCount: number;
  socketCount: number;
  exocarpColour: string | null;
  mesocarpColour: string | null;
  defectFlags: Record<string, boolean>;
}

function ScanResultModal({
  audit,
  tphCode,
  onClose,
  onSaved,
}: {
  audit: BunchAudit;
  tphCode: string | null;
  onClose: () => void;
  onSaved: (updated: BunchAudit) => void;
}) {
  const isAI = audit.mode === "AI";
  const photoUrl = `${API_BASE}/v1/bunch-audits/${audit.id}/photo`;
  const confidence = audit.rgcqRawSignals?.confidence;
  const latency = audit.rgcqRawSignals?.latency_ms;
  const model = audit.rgcqRawSignals?.model;
  const [editing, setEditing] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Per-bunch editable state · seeded from the persisted per_bunch when
  // the modal opens. Each row carries the socket bucket key (categorical
  // for the Mandor UI) and the raw OLC / mesocarp values.
  interface PerBunchEdit {
    bunch_index: number;
    socket_bucket: string;
    olc: Olc | null;
    mesocarp: Mesocarp | null;
    defect_flags: Record<string, boolean>;
  }
  const [perBunchEdit, setPerBunchEdit] = useState<PerBunchEdit[]>(() => {
    const src = audit.rgcqRawSignals?.per_bunch ?? [];
    if (src.length > 0) {
      return src.map((b) => ({
        bunch_index: b.bunch_index,
        socket_bucket: socketBucketFor(b.detached_sockets, b.detached_pct),
        olc: (b.olc ?? null) as Olc | null,
        mesocarp: (b.mesocarp ?? null) as Mesocarp | null,
        defect_flags: { ...(b.defect_flags ?? {}) },
      }));
    }
    // Fallback for legacy batches with no per_bunch — synthesize N rows.
    return Array.from({ length: audit.totalBunches }, (_, i) => ({
      bunch_index: i + 1,
      socket_bucket: socketBucketFor(
        Math.round((audit.socketCount ?? 0) / (audit.totalBunches || 1)),
      ),
      olc: (audit.exocarpColour ?? null) as Olc | null,
      mesocarp: (audit.mesocarpColour ?? null) as Mesocarp | null,
      defect_flags: {},
    }));
  });
  // Quality-state count inputs (9 categories, orthogonal to ripeness).
  const [qualityCounts, setQualityCounts] = useState<
    Record<QualityKey, number>
  >(() => {
    const src = audit.rgcqRawSignals?.quality_state_counts ?? {};
    const seed = {} as Record<QualityKey, number>;
    for (const k of QUALITY_KEYS) seed[k] = Number(src[k] ?? 0);
    return seed;
  });
  // After save, the parent hands back a fresh audit — re-seed from it so
  // the display reflects what the server actually persisted (rather than
  // whatever the pre-save local state was).
  useEffect(() => {
    const src = audit.rgcqRawSignals?.quality_state_counts ?? {};
    const seed = {} as Record<QualityKey, number>;
    for (const k of QUALITY_KEYS) seed[k] = Number(src[k] ?? 0);
    setQualityCounts(seed);
  }, [audit.id, audit.rgcqRawSignals?.quality_state_counts]);
  const qualitySum = QUALITY_KEYS.reduce((n, k) => n + qualityCounts[k], 0);
  const [edit, setEdit] = useState<EditState>(() => ({
    ripeCount: audit.ripeCount,
    underripeCount: audit.underripeCount,
    unripeCount: audit.unripeCount,
    overripeCount: audit.overripeCount,
    emptyCount: audit.emptyCount,
    rottenCount: audit.rottenCount,
    socketCount: audit.socketCount,
    exocarpColour: audit.exocarpColour,
    mesocarpColour: audit.mesocarpColour,
    defectFlags: {
      rotten: !!audit.defectFlags?.rotten,
      buah_peram: !!audit.defectFlags?.buah_peram,
      old: !!audit.defectFlags?.old,
      wet: !!audit.defectFlags?.wet,
      dirty: !!audit.defectFlags?.dirty,
      long_stalk: !!audit.defectFlags?.long_stalk,
      reject: !!audit.defectFlags?.reject,
    },
  }));

  const view = editing
    ? {
        ...audit,
        ripeCount: edit.ripeCount,
        underripeCount: edit.underripeCount,
        unripeCount: edit.unripeCount,
        overripeCount: edit.overripeCount,
        emptyCount: edit.emptyCount,
        rottenCount: edit.rottenCount,
        socketCount: edit.socketCount,
        exocarpColour: edit.exocarpColour,
        mesocarpColour: edit.mesocarpColour,
        defectFlags: edit.defectFlags,
      }
    : audit;
  const ripenessTotal =
    view.ripeCount +
    view.underripeCount +
    view.unripeCount +
    view.overripeCount +
    view.emptyCount +
    view.rottenCount;
  const ripenessPct = (n: number) =>
    ripenessTotal ? Math.round((n / ripenessTotal) * 100) : 0;
  const avgSockets = audit.totalBunches
    ? view.socketCount / audit.totalBunches
    : 0;
  const activeDefects = Object.entries(view.defectFlags ?? {})
    .filter(([, v]) => v === true)
    .map(([k]) => k.replace(/_/g, " "));
  const bucketSum =
    edit.ripeCount +
    edit.underripeCount +
    edit.unripeCount +
    edit.overripeCount +
    edit.emptyCount +
    edit.rottenCount;
  const sumOk = bucketSum === audit.totalBunches;

  const save = async () => {
    setSaveErr(null);
    setSaving(true);
    try {
      // Prefer per-bunch payload · server re-runs classifier authoritatively
      // and derives ripeCount/unripeCount/etc. from it.
      const per_bunch = perBunchEdit.map((r) => {
        const bucket = SOCKET_BUCKETS.find((b) => b.key === r.socket_bucket)!;
        return {
          bunch_index: r.bunch_index,
          detached_sockets: bucket.sockets,
          detached_pct: typeof bucket.pct === "number" ? bucket.pct : -1,
          olc: r.olc,
          mesocarp: r.mesocarp,
          defect_flags: r.defect_flags,
        };
      });
      const res = await api.patch<BunchAudit>(
        `/v1/bunch-audits/${audit.id}`,
        { per_bunch, quality_state_counts: qualityCounts },
      );
      onSaved(res.data);
      setEditing(false);
    } catch (err) {
      setSaveErr(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };
  const timestamp = new Date(audit.auditDate).toLocaleString("en-MY", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-5xl overflow-hidden rounded-xl border border-border bg-surface-2 shadow-2xl max-h-[90vh] flex flex-col"
      >
        {/* Header · timestamped */}
        <div className="flex items-center justify-between border-b border-border bg-surface-1 px-4 py-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[15px] font-semibold text-brand-charcoal">
              <IconScanEye size={17} className="text-brand-green-dark" />
              FFB Scan Result · {tphCode ?? audit.tphId.slice(0, 8)}
              <span
                className={`ml-1 rounded px-1.5 py-0.5 text-[9px] font-bold ${
                  isAI
                    ? "bg-brand-green text-white"
                    : "bg-slate-200 text-slate-800"
                }`}
              >
                {audit.mode}
              </span>
              {audit.readyForHarvest && (
                <span
                  className={`rounded px-1.5 py-0.5 text-[9px] font-bold ${
                    audit.readyForHarvest === "YES"
                      ? "bg-brand-green text-white"
                      : audit.readyForHarvest === "MIXED"
                        ? "bg-amber-500 text-white"
                        : "bg-slate-400 text-white"
                  }`}
                >
                  {audit.readyForHarvest === "YES"
                    ? "READY FOR HARVEST"
                    : audit.readyForHarvest === "MIXED"
                      ? "MIXED"
                      : "NOT READY"}
                </span>
              )}
            </div>
            <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-text-secondary">
              <IconClock size={11} />
              <span className="font-mono">{timestamp}</span>
              <span className="text-text-muted">·</span>
              <span className="font-mono text-[10px]">
                id {audit.id.slice(0, 8)}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {!editing && (
              <button
                onClick={() => setEditing(true)}
                className="rounded border border-brand-green bg-white px-2 py-1 text-[11px] font-semibold text-brand-green-dark hover:bg-brand-green hover:text-white transition-colors"
                title="Correct this grading"
              >
                Edit
              </button>
            )}
            <button
              onClick={onClose}
              className="rounded p-1.5 text-text-secondary hover:bg-surface-0 hover:text-text-primary"
              title="Close"
            >
              <IconX size={16} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="grid gap-4 overflow-y-auto p-4 md:grid-cols-[minmax(0,1fr)_320px]">
          {/* Left · photo panel */}
          <div className="rounded-lg border border-border bg-surface-0 overflow-hidden flex items-center justify-center min-h-[280px]">
            {isAI ? (
              <img
                src={photoUrl}
                alt={`Scan · ${audit.id}`}
                className="max-h-[520px] w-full object-contain"
                onError={(e) => {
                  const el = e.currentTarget;
                  el.style.display = "none";
                  el.parentElement!.innerHTML = `
                    <div class="flex flex-col items-center gap-2 p-8 text-text-secondary text-xs">
                      <div class="rounded-full bg-surface-1 p-3">📷</div>
                      <div>Scan photo not stored for this audit</div>
                      <div class="text-[10px] text-text-muted">Only newer AI scans persist the photo</div>
                    </div>`;
                }}
              />
            ) : (
              <div className="flex flex-col items-center gap-2 p-8 text-text-secondary text-xs">
                <div className="rounded-full bg-surface-1 p-3">
                  <IconPhoto size={22} className="text-text-muted" />
                </div>
                <div>Manual entry · no scan photo</div>
                <div className="text-[10px] text-text-muted">
                  Photos exist only for AI-mode audits
                </div>
              </div>
            )}
          </div>

          {/* Right · statistics */}
          <div className="space-y-3">
            {/* Total FFB card */}
            <div className="rounded-lg border border-border bg-surface-1 p-3">
              <div className="text-[10px] uppercase tracking-wide text-text-secondary">
                Total FFB detected {editing && (
                  <span className="ml-1 rounded bg-amber-100 px-1 py-px text-[8px] font-bold text-amber-800">
                    OVERRIDE AI
                  </span>
                )}
              </div>
              {editing ? (
                <div className="mt-1 flex items-center gap-2">
                  <button
                    onClick={() =>
                      setPerBunchEdit((prev) => {
                        if (prev.length <= 1) return prev;
                        return prev.slice(0, -1);
                      })
                    }
                    className="h-8 w-8 rounded border border-border bg-surface-2 text-lg font-bold hover:bg-surface-0 disabled:opacity-40"
                    disabled={perBunchEdit.length <= 1}
                  >
                    −
                  </button>
                  <input
                    type="number"
                    min={1}
                    value={perBunchEdit.length}
                    onChange={(e) => {
                      const n = Math.max(1, Number(e.target.value) || 1);
                      setPerBunchEdit((prev) => {
                        if (n === prev.length) return prev;
                        if (n < prev.length) {
                          return prev.slice(0, n);
                        }
                        const add: typeof prev = [];
                        for (let i = prev.length; i < n; i++) {
                          add.push({
                            bunch_index: i + 1,
                            socket_bucket: "gte5",
                            olc: null,
                            mesocarp: null,
                            defect_flags: {},
                          });
                        }
                        return [...prev, ...add];
                      });
                    }}
                    className="w-20 rounded border border-border bg-surface-2 py-1 text-center text-2xl font-bold tabular-nums text-brand-charcoal"
                  />
                  <button
                    onClick={() =>
                      setPerBunchEdit((prev) => [
                        ...prev,
                        {
                          bunch_index: prev.length + 1,
                          socket_bucket: "gte5",
                          olc: null,
                          mesocarp: null,
                          defect_flags: {},
                        },
                      ])
                    }
                    className="h-8 w-8 rounded border border-border bg-surface-2 text-lg font-bold hover:bg-surface-0"
                  >
                    +
                  </button>
                  <span className="text-[10px] text-text-secondary">
                    AI counted <b>{audit.totalBunches}</b>
                  </span>
                </div>
              ) : (
                <div className="mt-0.5 text-3xl font-bold tabular-nums text-brand-charcoal">
                  {audit.totalBunches}
                </div>
              )}
              {editing && (
                <div className="mt-2 rounded border border-brand-green/40 bg-brand-green/10 px-2 py-1 text-[10px] text-brand-green-dark">
                  Editing scoped to <b>{tphCode ?? "this TPH"}</b> only ·
                  changes save to this batch alone.
                </div>
              )}
            </div>

            {/* Ripeness breakdown */}
            <div className="rounded-lg border border-border bg-surface-1 p-3">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-[11px] font-semibold text-brand-charcoal">
                  Ripeness classification
                </div>
                {typeof confidence === "number" && !editing && (
                  <span className="rounded bg-brand-green/15 px-1.5 py-0.5 text-[9px] font-semibold text-brand-green-dark">
                    {(confidence * 100).toFixed(0)}%
                  </span>
                )}
                {editing && (
                  <span
                    className={`rounded px-1.5 py-0.5 text-[9px] font-semibold ${
                      sumOk
                        ? "bg-brand-green text-white"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    Σ {bucketSum}/{audit.totalBunches}
                  </span>
                )}
              </div>
              {editing ? (
                <div className="space-y-1.5">
                  <Stepper label="Ripe" value={edit.ripeCount} onChange={(v) => setEdit({ ...edit, ripeCount: v })} colour="bg-brand-green" />
                  <Stepper label="Underripe" value={edit.underripeCount} onChange={(v) => setEdit({ ...edit, underripeCount: v })} colour="bg-amber-500" />
                  <Stepper label="Unripe" value={edit.unripeCount} onChange={(v) => setEdit({ ...edit, unripeCount: v })} colour="bg-blue-500" />
                  <Stepper label="Overripe" value={edit.overripeCount} onChange={(v) => setEdit({ ...edit, overripeCount: v })} colour="bg-orange-600" />
                  <Stepper label="Empty" value={edit.emptyCount} onChange={(v) => setEdit({ ...edit, emptyCount: v })} colour="bg-slate-400" />
                  <Stepper label="Rotten" value={edit.rottenCount} onChange={(v) => setEdit({ ...edit, rottenCount: v })} colour="bg-red-600" />
                </div>
              ) : (
                <div className="space-y-1.5">
                  <Bar label="Ripe" n={view.ripeCount} pct={ripenessPct(view.ripeCount)} colour="bg-brand-green" />
                  <Bar label="Underripe" n={view.underripeCount} pct={ripenessPct(view.underripeCount)} colour="bg-amber-500" />
                  <Bar label="Unripe" n={view.unripeCount} pct={ripenessPct(view.unripeCount)} colour="bg-blue-500" />
                  <Bar label="Overripe" n={view.overripeCount} pct={ripenessPct(view.overripeCount)} colour="bg-orange-600" />
                  <Bar label="Empty" n={view.emptyCount} pct={ripenessPct(view.emptyCount)} colour="bg-slate-400" />
                  <Bar label="Rotten" n={view.rottenCount} pct={ripenessPct(view.rottenCount)} colour="bg-red-600" />
                </div>
              )}
            </div>

            {/* RGCQ compound signals */}
            <div className="rounded-lg border border-border bg-surface-1 p-3">
              <div className="mb-2 text-[11px] font-semibold text-brand-charcoal">
                RGCQ signals
              </div>
              {editing ? (
                <div className="space-y-2 text-[11px]">
                  <label className="block">
                    <span className="text-text-secondary">Outer layer</span>
                    <select
                      value={edit.exocarpColour ?? ""}
                      onChange={(e) => setEdit({ ...edit, exocarpColour: e.target.value || null })}
                      className="mt-0.5 w-full rounded border border-border bg-surface-2 px-2 py-1 text-[11px]"
                    >
                      <option value="">—</option>
                      <option value="black">black</option>
                      <option value="dark_purple">dark_purple</option>
                      <option value="reddish">reddish</option>
                      <option value="orange">orange</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className="text-text-secondary">Mesocarp</span>
                    <select
                      value={edit.mesocarpColour ?? ""}
                      onChange={(e) => setEdit({ ...edit, mesocarpColour: e.target.value || null })}
                      className="mt-0.5 w-full rounded border border-border bg-surface-2 px-2 py-1 text-[11px]"
                    >
                      <option value="">—</option>
                      <option value="yellow">yellow</option>
                      <option value="yellow_orange">yellow_orange</option>
                      <option value="orange">orange</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className="text-text-secondary">
                      Total sockets ({audit.totalBunches
                        ? (edit.socketCount / audit.totalBunches).toFixed(1)
                        : "0"}
                      /bunch · {edit.socketCount / (audit.totalBunches || 1) >= 5
                        ? "READY"
                        : edit.socketCount === 0
                          ? "NOT READY"
                          : "MIXED"})
                    </span>
                    <input
                      type="number"
                      min={0}
                      value={edit.socketCount}
                      onChange={(e) =>
                        setEdit({
                          ...edit,
                          socketCount: Math.max(0, Number(e.target.value) || 0),
                        })
                      }
                      className="mt-0.5 w-full rounded border border-border bg-surface-2 px-2 py-1 text-[11px] font-mono"
                    />
                  </label>
                </div>
              ) : (
                <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[11px]">
                  <Signal label="Outer layer" value={view.exocarpColour} />
                  <Signal label="Mesocarp" value={view.mesocarpColour} />
                  <Signal
                    label="Total sockets"
                    value={String(view.socketCount)}
                  />
                  <Signal
                    label="Avg / bunch"
                    value={avgSockets > 0 ? avgSockets.toFixed(1) : "0"}
                    hint={avgSockets >= 5 ? "≥ 5 threshold" : "< 5 threshold"}
                  />
                </dl>
              )}
            </div>

            {/* Quality-state · bunch counts per category. Orthogonal to
                ripeness (a RIPE bunch can also be `wet` or `long_stalk`). */}
            <div className="rounded-lg border border-border bg-surface-1 p-3">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-[11px] font-semibold text-brand-charcoal">
                  Quality state · bunch counts
                </div>
                <span
                  className={`rounded px-1.5 py-0.5 text-[9px] font-semibold ${
                    qualitySum === audit.totalBunches
                      ? "bg-brand-green/15 text-brand-green-dark"
                      : "bg-amber-100 text-amber-800"
                  }`}
                >
                  Σ {qualitySum} / {audit.totalBunches}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-1.5 text-[10px]">
                {QUALITY_KEYS.map((k) => (
                  <label
                    key={k}
                    className="flex items-center justify-between rounded border border-border bg-surface-2 px-1.5 py-1"
                  >
                    <span className="text-text-secondary">
                      {QUALITY_LABELS[k]}
                    </span>
                    {editing ? (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() =>
                            setQualityCounts((prev) => ({
                              ...prev,
                              [k]: Math.max(0, prev[k] - 1),
                            }))
                          }
                          className="h-5 w-5 rounded border border-border bg-surface-1 text-xs font-bold"
                        >
                          −
                        </button>
                        <input
                          type="number"
                          min={0}
                          value={qualityCounts[k]}
                          onChange={(e) =>
                            setQualityCounts((prev) => ({
                              ...prev,
                              [k]: Math.max(0, Number(e.target.value) || 0),
                            }))
                          }
                          className="w-10 rounded border border-border bg-surface-1 px-1 py-0.5 text-center text-[10px] font-mono tabular-nums"
                        />
                        <button
                          onClick={() =>
                            setQualityCounts((prev) => ({
                              ...prev,
                              [k]: prev[k] + 1,
                            }))
                          }
                          className="h-5 w-5 rounded border border-border bg-surface-1 text-xs font-bold"
                        >
                          +
                        </button>
                      </div>
                    ) : (
                      <span className="font-mono tabular-nums text-brand-charcoal">
                        {qualityCounts[k]}
                      </span>
                    )}
                  </label>
                ))}
              </div>
              {editing && qualitySum !== audit.totalBunches && (
                <div className="mt-1 text-[9px] text-amber-800">
                  Counts should sum to total bunches ({audit.totalBunches}) —
                  currently {qualitySum}.
                </div>
              )}
            </div>

            {/* Per-bunch grid — read-only view OR editable grid based on
                mode. Live classifier chip re-derives on every change.
                In edit mode Mandor can add/remove rows if the AI count
                doesn't match what they actually counted. */}
            {(() => {
              const rawConfidence = audit.rgcqRawSignals?.confidence ?? 0;
              // When AI is highly confident, hide the per-bunch signal
              // detail from the view — we trust the classifier. Editors
              // still see everything.
              const hideSignals = !editing && rawConfidence >= 0.9;
              if (
                (audit.rgcqRawSignals?.per_bunch?.length ?? 0) === 0 &&
                !editing
              )
                return null;
              return (
              <div className="rounded-lg border border-border bg-surface-1 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <div className="text-[11px] font-semibold text-brand-charcoal">
                    Per-bunch · ripeness matrix (flowchart)
                  </div>
                  <span className="text-[9px] text-text-secondary">
                    {editing
                      ? "edit signals · class auto-derives"
                      : hideSignals
                        ? `AI confidence ${(rawConfidence * 100).toFixed(0)}% · signals hidden`
                        : "auto-classified"}
                  </span>
                </div>
                {editing && perBunchEdit.length !== audit.totalBunches && (
                  <div className="mb-2 rounded border border-amber-300 bg-amber-50 p-1.5 text-[10px] text-amber-900">
                    <b>AI counted {audit.totalBunches}</b> · you have{" "}
                    <b>{perBunchEdit.length}</b> {perBunchEdit.length === 1 ? "row" : "rows"}
                    . Saving will overwrite the AI count.
                  </div>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full text-[10px]">
                    <thead className="text-[9px] uppercase text-text-secondary">
                      <tr>
                        <th className="pb-1 text-left">Bunch</th>
                        {!hideSignals && <th className="pb-1 text-left">OLC</th>}
                        {!hideSignals && (
                          <th className="pb-1 text-left">Mesocarp</th>
                        )}
                        {!hideSignals && (
                          <th className="pb-1 text-left">Sockets</th>
                        )}
                        <th className="pb-1 text-left">Class</th>
                        {editing && <th className="pb-1 w-6"></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {editing
                        ? perBunchEdit.map((r, idx) => {
                            const bucket = SOCKET_BUCKETS.find(
                              (b) => b.key === r.socket_bucket,
                            )!;
                            const cls = classifyBunch({
                              detachedSockets: bucket.sockets,
                              detachedPct: bucket.pct,
                              olc: r.olc,
                              mesocarp: r.mesocarp,
                            });
                            const upd = (patch: Partial<PerBunchEdit>) => {
                              setPerBunchEdit((prev) => {
                                const next = [...prev];
                                next[idx] = { ...next[idx], ...patch };
                                return next;
                              });
                            };
                            return (
                              <tr
                                key={r.bunch_index}
                                className="border-t border-border align-top"
                              >
                                <td className="py-1 font-mono">#{r.bunch_index}</td>
                                <td className="py-1">
                                  <select
                                    value={r.olc ?? ""}
                                    onChange={(e) =>
                                      upd({ olc: (e.target.value || null) as Olc | null })
                                    }
                                    className="w-full rounded border border-border bg-surface-2 px-1 py-0.5 text-[10px]"
                                  >
                                    <option value="">—</option>
                                    {OLC_UI_OPTIONS.map((o) => (
                                      <option key={o.value} value={o.value}>
                                        {o.label}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                                <td className="py-1">
                                  <select
                                    value={r.mesocarp ?? ""}
                                    onChange={(e) =>
                                      upd({
                                        mesocarp: (e.target.value || null) as Mesocarp | null,
                                      })
                                    }
                                    className="w-full rounded border border-border bg-surface-2 px-1 py-0.5 text-[10px]"
                                  >
                                    <option value="">—</option>
                                    {MESOCARP_UI_OPTIONS.map((o) => (
                                      <option key={o.value} value={o.value}>
                                        {o.label}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                                <td className="py-1">
                                  <select
                                    value={r.socket_bucket}
                                    onChange={(e) =>
                                      upd({ socket_bucket: e.target.value })
                                    }
                                    className="w-full rounded border border-border bg-surface-2 px-1 py-0.5 text-[10px] font-mono"
                                  >
                                    {SOCKET_BUCKETS.map((b) => (
                                      <option key={b.key} value={b.key}>
                                        {b.label}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                                <td className="py-1">
                                  <RipenessChip class_={cls.ripeness} />
                                </td>
                                <td className="py-1">
                                  <button
                                    onClick={() =>
                                      setPerBunchEdit((prev) =>
                                        prev
                                          .filter((_, i) => i !== idx)
                                          .map((row, i) => ({
                                            ...row,
                                            bunch_index: i + 1,
                                          })),
                                      )
                                    }
                                    className="text-text-secondary hover:text-red-700 text-sm"
                                    title={`Remove bunch #${r.bunch_index}`}
                                    disabled={perBunchEdit.length <= 1}
                                  >
                                    ✕
                                  </button>
                                </td>
                              </tr>
                            );
                          })
                        : (audit.rgcqRawSignals?.per_bunch ?? []).map((b) => (
                            <tr key={b.bunch_index} className="border-t border-border">
                              <td className="py-1 font-mono">#{b.bunch_index}</td>
                              {!hideSignals && (
                                <td className="py-1 font-mono">
                                  {b.olc ?? "—"}
                                </td>
                              )}
                              {!hideSignals && (
                                <td className="py-1 font-mono">
                                  {b.mesocarp ?? "—"}
                                </td>
                              )}
                              {!hideSignals && (
                                <td className="py-1 text-right font-mono tabular-nums">
                                  {b.detached_sockets}
                                </td>
                              )}
                              <td className="py-1">
                                <RipenessChip class_={b.classified_as ?? "—"} />
                              </td>
                            </tr>
                          ))}
                    </tbody>
                    {editing && (
                      <tfoot className="border-t border-border text-[9px] text-text-secondary">
                        <tr>
                          <td colSpan={6} className="pt-1.5">
                            <button
                              onClick={() =>
                                setPerBunchEdit((prev) => [
                                  ...prev,
                                  {
                                    bunch_index: prev.length + 1,
                                    socket_bucket: "gte5",
                                    olc: null,
                                    mesocarp: null,
                                    defect_flags: {},
                                  },
                                ])
                              }
                              className="rounded border border-brand-green px-2 py-0.5 text-[10px] font-semibold text-brand-green-dark hover:bg-brand-green hover:text-white"
                            >
                              + Add bunch
                            </button>
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={6} className="pt-1">
                            {(() => {
                              const counts: Record<RipenessClass, number> = {
                                RIPE: 0,
                                UNRIPE: 0,
                                OVERRIPE: 0,
                                EMPTY: 0,
                              };
                              for (const r of perBunchEdit) {
                                const bucket = SOCKET_BUCKETS.find(
                                  (b) => b.key === r.socket_bucket,
                                )!;
                                const cls = classifyBunch({
                                  detachedSockets: bucket.sockets,
                                  detachedPct: bucket.pct,
                                  olc: r.olc,
                                  mesocarp: r.mesocarp,
                                });
                                counts[cls.ripeness]++;
                              }
                              return (
                                <>
                                  Live aggregate · RIPE {counts.RIPE} · UNRIPE{" "}
                                  {counts.UNRIPE} · OVERRIPE {counts.OVERRIPE}{" "}
                                  · EMPTY {counts.EMPTY}
                                </>
                              );
                            })()}
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>
              );
            })()}

            {/* Defect flags */}
            <div className="rounded-lg border border-border bg-surface-1 p-3">
              <div className="mb-2 text-[11px] font-semibold text-brand-charcoal">
                Defect flags
              </div>
              {editing ? (
                <div className="grid grid-cols-2 gap-1">
                  {DEFECT_KEYS.map((k) => (
                    <label
                      key={k}
                      className="flex items-center gap-1.5 text-[11px]"
                    >
                      <input
                        type="checkbox"
                        checked={!!edit.defectFlags[k]}
                        onChange={(e) =>
                          setEdit({
                            ...edit,
                            defectFlags: {
                              ...edit.defectFlags,
                              [k]: e.target.checked,
                            },
                          })
                        }
                      />
                      <span>{k.replace(/_/g, " ")}</span>
                    </label>
                  ))}
                </div>
              ) : activeDefects.length ? (
                <div className="flex flex-wrap gap-1">
                  {activeDefects.map((d) => (
                    <span
                      key={d}
                      className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-900"
                    >
                      {d}
                    </span>
                  ))}
                </div>
              ) : (
                <div className="text-[11px] text-text-secondary">
                  No defects flagged
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Edit action bar */}
        {editing && (
          <div className="flex items-center justify-between border-t border-border bg-surface-1 px-4 py-2">
            <div className="text-[11px] text-text-secondary">
              {saveErr ? (
                <span className="text-red-700">{saveErr}</span>
              ) : (
                <span>
                  Corrections are stored alongside the AI original for future
                  model training.
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setEditing(false);
                  setSaveErr(null);
                }}
                className="rounded border border-border bg-surface-2 px-3 py-1 text-[11px] font-semibold text-text-primary hover:bg-surface-0"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="rounded bg-brand-green px-3 py-1 text-[11px] font-semibold text-white hover:bg-brand-green-dark disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save corrections"}
              </button>
            </div>
          </div>
        )}

        {/* Footer · model / latency provenance */}
        {isAI && model && (
          <div className="flex items-center justify-between border-t border-border bg-surface-0 px-4 py-2 text-[10px] text-text-secondary">
            <div className="font-mono">Model: {model}</div>
            <div className="font-mono">
              {typeof latency === "number" && `${latency} ms · `}
              {typeof confidence === "number" &&
                `confidence ${(confidence * 100).toFixed(0)}%`}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Bar({
  label,
  n,
  pct,
  colour,
}: {
  label: string;
  n: number;
  pct: number;
  colour: string;
}) {
  return (
    <div>
      <div className="flex justify-between text-[10px] text-text-secondary">
        <span>{label}</span>
        <span className="font-mono">
          {n} <span className="text-text-muted">·</span> {pct}%
        </span>
      </div>
      <div className="mt-0.5 h-1.5 w-full overflow-hidden rounded bg-surface-0">
        <div
          className={`h-full ${colour} transition-all`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

/**
 * NFC card action side panel · slides in from the right; three modes:
 *   view   · full read-only detail
 *   edit   · append a note (contents edit is a bigger job for later)
 *   reject · confirm + reason → PATCH status → CANCELLED
 *
 * SCANNED / RECONCILED cards can still be Viewed but Edit / Reject are
 * disabled at the row level (already read-only downstream).
 */
function NfcCardActionPanel({
  card,
  mode,
  onClose,
  onSaved,
}: {
  card: NfcCard;
  mode: "view" | "edit" | "reject";
  onClose: () => void;
  onSaved: (msg: string) => void;
}) {
  const [reason, setReason] = useState("");
  const [noteAdd, setNoteAdd] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const gc = card.gradingSummary?.grade_counts ?? {};

  const doAction = async () => {
    setErr(null);
    setBusy(true);
    try {
      if (mode === "reject") {
        if (!reason.trim()) {
          setErr("Enter a rejection reason.");
          setBusy(false);
          return;
        }
        await api.patch(`/v1/nfc-delivery-cards/${card.id}/status`, {
          status: "CANCELLED",
          reason: reason.trim(),
        });
        onSaved(`NFC card ${card.cardCode} rejected ✓`);
      } else if (mode === "edit") {
        if (!noteAdd.trim()) {
          setErr("Nothing to save.");
          setBusy(false);
          return;
        }
        // Preserve status; use the reason field to append a note.
        await api.patch(`/v1/nfc-delivery-cards/${card.id}/status`, {
          status: card.status,
          reason: noteAdd.trim(),
        });
        onSaved(`Note added to ${card.cardCode} ✓`);
      }
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex h-full w-full max-w-md flex-col overflow-y-auto border-l border-border bg-surface-2 shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border bg-surface-1 px-4 py-3">
          <div>
            <div className="flex items-center gap-2 text-[14px] font-semibold text-brand-charcoal">
              {mode === "view"
                ? "View NFC card"
                : mode === "edit"
                  ? "Edit NFC card"
                  : "Reject NFC card"}
              <span
                className={`rounded px-1.5 py-0.5 text-[9px] font-bold ${
                  card.status === "SCANNED"
                    ? "bg-emerald-100 text-emerald-800"
                    : card.status === "WRITTEN"
                      ? "bg-blue-100 text-blue-800"
                      : card.status === "CANCELLED"
                        ? "bg-red-100 text-red-800"
                        : "bg-slate-100 text-slate-600"
                }`}
              >
                {card.status}
              </span>
            </div>
            <div className="text-[11px] font-mono text-text-secondary">
              {card.cardCode}
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded p-1.5 text-text-secondary hover:bg-surface-0 hover:text-text-primary"
            title="Close"
          >
            <IconX size={16} />
          </button>
        </div>

        {/* Body — always show the details; extra controls for edit/reject */}
        <div className="flex-1 space-y-3 p-4 text-xs">
          <div className="grid grid-cols-2 gap-2">
            <Kpi
              icon={<IconLeaf size={14} className="text-brand-green-dark" />}
              label="Total bunches"
              value={String(card.totalBunches)}
            />
            <Kpi
              icon={<IconCheck size={14} className="text-brand-green-dark" />}
              label="Ready for harvest"
              value={String(card.totalReadyForHarvest)}
            />
            <Kpi
              icon={<IconAlertTriangle size={14} className="text-amber-700" />}
              label="Pre-rejected"
              value={String(card.totalPreRejected)}
            />
            <Kpi
              icon={<IconRefresh size={14} className="text-text-secondary" />}
              label="Avg sockets / bunch"
              value={card.gradingSummary?.avg_socket_count?.toFixed(1) ?? "—"}
            />
          </div>

          <div className="rounded-lg border border-border bg-surface-1 p-3">
            <div className="mb-1.5 text-[11px] font-semibold text-brand-charcoal">
              Grading breakdown
            </div>
            <div className="grid grid-cols-3 gap-1.5 text-[10px]">
              {Object.entries(gc).map(([k, v]) => (
                <div
                  key={k}
                  className="rounded bg-surface-0 px-2 py-1 flex items-center justify-between"
                >
                  <span className="text-text-secondary uppercase">{k}</span>
                  <span className="font-mono tabular-nums">{v}</span>
                </div>
              ))}
              {!Object.keys(gc).length && (
                <div className="col-span-3 text-text-secondary">
                  No grading counts on this card.
                </div>
              )}
            </div>
          </div>

          <div className="rounded-lg border border-border bg-surface-1 p-3 text-[11px]">
            <div className="mb-1 font-semibold text-brand-charcoal">Timeline</div>
            <div className="text-text-secondary space-y-0.5">
              <div>
                Written ·{" "}
                <span className="font-mono">
                  {new Date(card.writtenAt).toLocaleString("en-MY")}
                </span>
              </div>
              {card.scannedAt && (
                <div>
                  Scanned at mill ·{" "}
                  <span className="font-mono">
                    {new Date(card.scannedAt).toLocaleString("en-MY")}
                  </span>
                </div>
              )}
            </div>
          </div>

          {mode === "edit" && (
            <div className="rounded-lg border border-brand-green bg-brand-green/10 p-3 space-y-2">
              <div className="text-[11px] font-semibold text-brand-charcoal">
                Add a note to this card
              </div>
              <textarea
                rows={3}
                value={noteAdd}
                onChange={(e) => setNoteAdd(e.target.value)}
                placeholder="e.g. Driver confirmed 3 bunches missed on second pickup — added manually"
                className="w-full rounded border border-border bg-surface-2 p-2 text-[11px]"
              />
            </div>
          )}

          {mode === "reject" && (
            <div className="rounded-lg border border-red-300 bg-red-50 p-3 space-y-2">
              <div className="text-[11px] font-semibold text-red-800">
                Reject this NFC card
              </div>
              <div className="text-[10px] text-red-800">
                The card will be marked <b>CANCELLED</b> and cannot be scanned
                at the mill. This action is not reversible from the UI.
              </div>
              <textarea
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Rejection reason · e.g. wrong bunches selected, mixed with unripe from another block"
                className="w-full rounded border border-red-300 bg-white p-2 text-[11px]"
              />
            </div>
          )}

          {err && (
            <div className="rounded border border-red-300 bg-red-50 p-2 text-[11px] text-red-800">
              {err}
            </div>
          )}
        </div>

        {/* Footer action bar */}
        {(mode === "edit" || mode === "reject") && (
          <div className="flex items-center justify-end gap-2 border-t border-border bg-surface-1 px-4 py-2">
            <button
              onClick={onClose}
              className="rounded border border-border bg-surface-2 px-3 py-1 text-[11px] font-semibold text-text-primary hover:bg-surface-0"
            >
              Cancel
            </button>
            <button
              onClick={doAction}
              disabled={busy}
              className={`rounded px-3 py-1 text-[11px] font-semibold text-white disabled:opacity-50 ${
                mode === "reject"
                  ? "bg-red-600 hover:bg-red-700"
                  : "bg-brand-green hover:bg-brand-green-dark"
              }`}
            >
              {busy
                ? mode === "reject"
                  ? "Rejecting…"
                  : "Saving…"
                : mode === "reject"
                  ? "Confirm reject"
                  : "Save note"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function RipenessChip({ class_ }: { class_: string }) {
  const styles: Record<string, string> = {
    RIPE: "bg-brand-green text-white",
    UNRIPE: "bg-blue-500 text-white",
    OVERRIPE: "bg-orange-600 text-white",
    EMPTY: "bg-slate-500 text-white",
  };
  const cls = styles[class_] ?? "bg-slate-200 text-slate-700";
  return (
    <span className={`rounded px-1.5 py-0.5 text-[9px] font-bold ${cls}`}>
      {class_}
    </span>
  );
}

function Stepper({
  label,
  value,
  onChange,
  colour,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
  colour: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <span
        className={`inline-block h-2 w-2 rounded-full ${colour}`}
        aria-hidden
      />
      <span className="flex-1 text-[11px] text-text-primary">{label}</span>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onChange(Math.max(0, value - 1))}
          className="h-6 w-6 rounded border border-border bg-surface-2 text-xs font-bold text-text-primary hover:bg-surface-0"
          title={`Decrement ${label}`}
        >
          −
        </button>
        <input
          type="number"
          min={0}
          value={value}
          onChange={(e) => onChange(Math.max(0, Number(e.target.value) || 0))}
          className="w-12 rounded border border-border bg-surface-2 px-1 py-0.5 text-center text-[11px] font-mono tabular-nums"
        />
        <button
          onClick={() => onChange(value + 1)}
          className="h-6 w-6 rounded border border-border bg-surface-2 text-xs font-bold text-text-primary hover:bg-surface-0"
          title={`Increment ${label}`}
        >
          +
        </button>
      </div>
    </div>
  );
}

function Signal({
  label,
  value,
  hint,
}: {
  label: string;
  value: string | null;
  hint?: string;
}) {
  return (
    <>
      <dt className="text-text-secondary">{label}</dt>
      <dd className="text-right font-mono text-brand-charcoal">
        {value ?? "—"}
        {hint && (
          <span className="ml-1 text-[9px] text-text-muted">({hint})</span>
        )}
      </dd>
    </>
  );
}

function Kpi({
  icon,
  label,
  value,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="rounded border border-border bg-surface-2 p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-text-secondary">
        {icon}
        {label}
      </div>
      <div className="mt-1 text-xl font-bold tabular-nums text-text-primary">
        {value}
      </div>
      {hint && <div className="text-[10px] text-text-secondary">{hint}</div>}
    </div>
  );
}
